import type { FredInbox, FredInboxItem, UserRef } from "@nmt/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link } from "react-router";
import { del, errorMessage, get, post } from "../api";
import { Alert, Badge, Button, Empty, Loading, PageHeader, Panel, PanelHeader, ago, cx } from "../components/ui";
import { useUsers } from "../lookups";

const STATUS: Record<FredInboxItem["status"], { label: string; tone: "ok" | "wait" | "off" | "brand" }> = {
  held: { label: "Waiting for a look", tone: "wait" },
  queued: { label: "In line", tone: "off" },
  working: { label: "Fred's working on it", tone: "brand" },
  done: { label: "Done", tone: "ok" },
  needs_answer: { label: "Needs your answer", tone: "wait" },
  couldnt_handle: { label: "Couldn't handle", tone: "off" },
  failed: { label: "Didn't finish", tone: "off" },
  dropped: { label: "Spam (dropped)", tone: "off" },
  ignored: { label: "Ignored", tone: "off" },
};
const TRUST: Record<FredInboxItem["trust"], string> = { staff: "Staff", contact: "Contact", allowed: "Allowed sender", outside: "Outside" };

const openFred = (conversationId: string) => window.dispatchEvent(new CustomEvent("nmt:open-fred", { detail: { conversationId } }));

/** Email sent to Fred: what he did, what he's asking, and (for admins) outside mail waiting for a look. */
export function FredInboxPage() {
  const qc = useQueryClient();
  const [all, setAll] = useState(false);
  const inbox = useQuery({ queryKey: ["fred-inbox", all], queryFn: () => get<FredInbox>(`/api/fred/inbox${all ? "?all=1" : ""}`), refetchInterval: 20_000 });
  const refresh = () => {
    void qc.invalidateQueries({ queryKey: ["fred-inbox"] });
    void qc.invalidateQueries({ queryKey: ["fred-inbox-count"] });
  };
  const data = inbox.data;
  const held = data?.items.filter((i) => i.status === "held") ?? [];
  const rest = data?.items.filter((i) => i.status !== "held") ?? [];
  const asking = rest.filter((i) => i.status === "needs_answer");
  const others = rest.filter((i) => i.status !== "needs_answer");

  return (
    <div className="flex flex-col gap-5">
      <PageHeader
        title="Fred's inbox"
        description={data?.address ? `Email invoices, insurance certificates, scopes, and photos to ${data.address}; Fred files them and tells you what he did or asks.` : "Email to Fred isn't set up on this server yet."}
        action={
          data?.canManage && (
            <div className="flex rounded-[var(--radius-control)] bg-mist p-0.5 text-sm">
              {[false, true].map((v) => (
                <button key={String(v)} className={cx("rounded px-3 py-1.5 font-medium", all === v ? "bg-white shadow-sm" : "text-muted")} onClick={() => setAll(v)}>
                  {v ? "Everyone's" : "Mine"}
                </button>
              ))}
            </div>
          )
        }
      />
      {inbox.isLoading ? (
        <Loading />
      ) : inbox.error ? (
        <Alert>{errorMessage(inbox.error)}</Alert>
      ) : (
        <>
          {data?.canManage && held.length > 0 && (
            <Panel>
              <PanelHeader title={`From outside, not yet looked at (${held.length})`} description="From senders Fred doesn't know (or who didn't pass the sender checks). Fred hasn't read them. Let him look, or ignore them." />
              <ul>
                {held.map((i) => (
                  <HeldRow key={i.id} item={i} onDone={refresh} />
                ))}
              </ul>
            </Panel>
          )}
          {asking.length > 0 && (
            <Panel>
              <PanelHeader title={`Needs your answer (${asking.length})`} />
              <ul>
                {asking.map((i) => (
                  <Row key={i.id} item={i} showOwner={all} onDone={refresh} />
                ))}
              </ul>
            </Panel>
          )}
          <Panel>
            <PanelHeader title="Recent" />
            {others.length ? (
              <ul>
                {others.map((i) => (
                  <Row key={i.id} item={i} showOwner={all} onDone={refresh} />
                ))}
              </ul>
            ) : (
              <Empty>{data?.address ? `Nothing yet. Forward something to ${data.address}.` : "Nothing yet."}</Empty>
            )}
          </Panel>
          {data?.canManage && <Senders allowed={data.allowedSenders} onDone={refresh} />}
        </>
      )}
    </div>
  );
}

function From({ item }: { item: FredInboxItem }) {
  return (
    <span className="text-xs text-muted">
      {item.fromName ? `${item.fromName} <${item.fromAddress}>` : item.fromAddress} · {TRUST[item.trust]}
      {!item.verified && " (not verified)"} · {ago(item.receivedAt)}
    </span>
  );
}

function Row({ item, showOwner, onDone }: { item: FredInboxItem; showOwner: boolean; onDone: () => void }) {
  const s = STATUS[item.status];
  const settle = useMutation({ mutationFn: () => post(`/api/fred/inbox/${item.id}/done`, {}), onSuccess: onDone });
  const retry = useMutation({ mutationFn: () => post(`/api/fred/inbox/${item.id}/look`, {}), onSuccess: onDone });
  return (
    <li className="flex flex-col gap-1.5 border-b border-mist px-5 py-3 last:border-0">
      <div className="flex flex-wrap items-center gap-2">
        <span className="min-w-0 flex-1 truncate font-semibold">{item.subject || "(no subject)"}</span>
        <Badge tone={s.tone}>{s.label}</Badge>
      </div>
      <From item={item} />
      {item.status === "needs_answer" && item.question && <div className="rounded-[var(--radius-control)] bg-wait-wash px-3 py-2 text-sm text-ink">{item.question}</div>}
      {item.summary && <p className="text-sm text-ink-soft">{item.summary}</p>}
      {item.note && <p className="text-xs text-muted">{item.note}</p>}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
        {item.job && (
          <Link to={`/jobs/${item.job.id}`} className="font-medium text-brand hover:underline">
            Job #{item.job.number} · {item.job.customerName}
          </Link>
        )}
        {item.attachments.length > 0 && <span className="text-muted">{item.attachments.map((a) => a.name).join(", ")}</span>}
        {showOwner && item.owner && <span className="text-muted">For {item.owner.name}</span>}
      </div>
      <div className="flex flex-wrap gap-2">
        {item.conversationId && (
          <Button variant={item.status === "needs_answer" ? "primary" : "secondary"} onClick={() => openFred(item.conversationId!)}>
            {item.status === "needs_answer" ? "Answer Fred" : "Open with Fred"}
          </Button>
        )}
        {item.status === "needs_answer" && (
          <Button busy={settle.isPending} onClick={() => settle.mutate()}>
            Already sorted
          </Button>
        )}
        {["failed", "couldnt_handle"].includes(item.status) && (
          <Button busy={retry.isPending} onClick={() => retry.mutate()}>
            Try again
          </Button>
        )}
      </div>
      {(settle.error || retry.error) && <Alert>{errorMessage(settle.error ?? retry.error)}</Alert>}
    </li>
  );
}

function HeldRow({ item, onDone }: { item: FredInboxItem; onDone: () => void }) {
  const users = useUsers();
  const [forUser, setForUser] = useState("");
  const look = useMutation({ mutationFn: (allowSender: boolean) => post(`/api/fred/inbox/${item.id}/look`, { userId: forUser || null, allowSender }), onSuccess: onDone });
  const ignore = useMutation({ mutationFn: () => post(`/api/fred/inbox/${item.id}/ignore`, {}), onSuccess: onDone });
  return (
    <li className="flex flex-col gap-1.5 border-b border-mist px-5 py-3 last:border-0">
      <span className="font-semibold">{item.subject || "(no subject)"}</span>
      <From item={item} />
      {item.note && <p className="text-xs text-danger">{item.note}</p>}
      {item.preview && <p className="rounded-[var(--radius-control)] bg-mist/60 px-3 py-2 text-sm text-ink-soft">{item.preview}</p>}
      {item.attachments.length > 0 && <span className="text-sm text-muted">Attached: {item.attachments.map((a) => a.name).join(", ")}</span>}
      <div className="flex flex-wrap items-center gap-2">
        <select className="rounded-[var(--radius-control)] border border-line bg-white px-2 py-1.5 text-base sm:text-sm" value={forUser} onChange={(e) => setForUser(e.target.value)} aria-label="For whom">
          <option value="">For me</option>
          {(users.data ?? [])
            .filter((u: UserRef) => u.active)
            .map((u: UserRef) => (
              <option key={u.id} value={u.id}>
                For {u.name}
              </option>
            ))}
        </select>
        <Button variant="primary" busy={look.isPending && look.variables === false} onClick={() => look.mutate(false)}>
          Let Fred look
        </Button>
        <Button busy={look.isPending && look.variables === true} onClick={() => look.mutate(true)}>
          Let Fred look, and always allow this sender
        </Button>
        <Button busy={ignore.isPending} onClick={() => ignore.mutate()}>
          Ignore
        </Button>
      </div>
      {(look.error || ignore.error) && <Alert>{errorMessage(look.error ?? ignore.error)}</Alert>}
    </li>
  );
}

function Senders({ allowed, onDone }: { allowed: string[]; onDone: () => void }) {
  const [sender, setSender] = useState("");
  const add = useMutation({ mutationFn: () => post("/api/fred/inbox/senders", { sender }), onSuccess: () => (setSender(""), onDone()) });
  const remove = useMutation({ mutationFn: (s: string) => del(`/api/fred/inbox/senders?sender=${encodeURIComponent(s)}`), onSuccess: onDone });
  return (
    <Panel>
      <PanelHeader title="Allowed outside senders" description="Email from these goes straight to Fred (when it passes the sender checks), like a staff member's or a contact's. An address, or @domain.com for everyone there." />
      <div className="flex flex-col gap-3 px-5 py-4">
        {allowed.length ? (
          <ul className="flex flex-wrap gap-2">
            {allowed.map((s) => (
              <li key={s} className="flex items-center gap-1.5 rounded-full bg-mist px-3 py-1 text-sm">
                {s}
                <button className="text-muted hover:text-danger" aria-label={`Remove ${s}`} onClick={() => remove.mutate(s)}>
                  ✕
                </button>
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-muted">None yet. Staff and contacts in the CRM don't need to be added.</p>
        )}
        <form
          className="flex gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            if (sender.trim()) add.mutate();
          }}
        >
          <input className="w-full max-w-sm rounded-[var(--radius-control)] border border-line bg-white px-2.5 py-1.5 text-base sm:text-sm" placeholder="agent@insurance.com or @insurance.com" value={sender} onChange={(e) => setSender(e.target.value)} />
          <Button type="submit" busy={add.isPending}>
            Add
          </Button>
        </form>
        {(add.error || remove.error) && <Alert>{errorMessage(add.error ?? remove.error)}</Alert>}
      </div>
    </Panel>
  );
}
